import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EMPTY, from } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  mergeMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { DialogConfirmPromptService } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ListValuesDialogService } from '@libs/src/list-values-dialog/list-values-dialog.service';
import { ToastService } from '@libs/src/toast/toast.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Group } from '@libs/src/models/group.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { UploadCSVUniqueColumn } from '@libs/src/utilities/csv.utilities';
import { areWordsSimilar } from '@libs/src/utilities/words.helper';
import {
  accountFieldOptions,
  accountFieldsList,
  buildValueTransformations,
  createNewUniqueColumn,
  getColumnTranslation,
  getDestinationPath,
  groupFieldOptions,
  MappingOption,
  role,
  VisibleUniqueColumn,
} from '@main-client/src/app/attribute-mapping/attribute-mapping.helper';
import {
  AttributeMappingPostBody,
  AttributeMappingPutBody,
  UniqueColumn,
} from '@main-client/src/app/invite-csv/invite-csv-view/invite-csv-view.interface';

import { AttributeMappingDrawerService } from '../attribute-mapping-drawer/attribute-mapping-drawer.service';

import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import uniq from 'lodash-es/uniq';
import uniqBy from 'lodash-es/uniqBy';

interface AttributeMapResponse {
  group: Group;
  hasBeenReviewed: boolean;
  updatedColumn: VisibleUniqueColumn;
}

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-csv-attribute-mappings',
  templateUrl: './csv-attribute-mappings.component.html',
})
export class CsvAttributeMappingsComponent implements OnInit {
  @Input() tenant: Tenant;
  @Input() uploadedData: UploadCSVUniqueColumn[];
  @Input() group: Group;
  @Output() viewChange = new EventEmitter<string>();
  fileColumnTitles: string[];
  columnsWithUniqueValues: UniqueColumn[];
  columnsWithUpdatedSourcePaths: UniqueColumn[];
  isLoading: boolean;
  visibleUniqueColumns: VisibleUniqueColumn[];
  uniqueColumns: UniqueColumn[];
  showBanner = false;
  unReviewedFields = '';
  newAutomappedColumns: UniqueColumn[];
  constructor(
    private readonly attributeMappingDrawerService: AttributeMappingDrawerService,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly dialogConfirmPrompt: DialogConfirmPromptService,
    private readonly groupsBackend: GroupsBackend,
    private readonly listValuesDialog: ListValuesDialogService,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.fileColumnTitles = this.uploadedData.map(
      (column) => column.columnName,
    );
    this.groupsBackend.v3
      .getGroupAttributeMappings(this.group._id, 'admin-accounts-csv')
      .pipe(
        tap(({ attributeMappings }) => {
          const mappingOptions = this.getAllMappingOptions();
          const newColumns = this.uploadedData.map(
            (uniqueColumn: UploadCSVUniqueColumn) => {
              const matchingColumn = mappingOptions
                .sort((a, b) => b.value.length - a.value.length)
                .find((option) =>
                  areWordsSimilar(option.value, uniqueColumn.columnName),
                );
              return createNewUniqueColumn(uniqueColumn, matchingColumn?.value);
            },
          );
          const existingUploadedColumns = this.uploadedData.reduce(
            (acc, uniqueColumn: UploadCSVUniqueColumn) => {
              const existingAttributeBySourcePath = attributeMappings?.find(
                (mapping) => uniqueColumn.columnName === mapping.source_path[0],
              );
              const column = newColumns.find(
                (newColumn) => uniqueColumn.columnName === newColumn.name,
              );
              const existingAttributeByDestinationPath = column.translation
                ? attributeMappings?.find(
                    (mapping) =>
                      getDestinationPath(this.group, column).type ===
                      mapping.destination_path,
                  )
                : {};
              const existingAttribute = {
                ...(existingAttributeBySourcePath || {}),
                ...(existingAttributeByDestinationPath || {}),
              };
              if (!isEmpty(existingAttribute)) {
                return [
                  ...acc,
                  {
                    ...uniqueColumn,
                    attributeId: existingAttribute._id,
                    attribute_translation: existingAttribute.destination_path,
                  },
                ];
              }
              return acc;
            },
            [],
          );
          let existingColumns: UniqueColumn[] = [];
          if (!isEmpty(existingUploadedColumns)) {
            existingColumns = existingUploadedColumns.map((uniqueColumn) => {
              const isAccountField = accountFieldOptions.some(
                (field) => uniqueColumn.attribute_translation === field.value,
              );
              const existingColumn = {
                attributeId: uniqueColumn.attributeId,
                ...getColumnTranslation(
                  attributeMappings,
                  this.group,
                  uniqueColumn,
                ),
                ...(isAccountField && {
                  translation: uniqueColumn.attribute_translation,
                }),
                ...('groups.role' === uniqueColumn.attribute_translation && {
                  translation: role,
                }),
              };
              return existingColumn.name !== uniqueColumn.columnName
                ? {
                    ...existingColumn,
                    hasSourcePathChanged: true,
                    name: uniqueColumn.columnName,
                  }
                : existingColumn;
            });
          }
          this.newAutomappedColumns = newColumns.filter((column) => {
            const hasTranslation = !!column.translation;
            if (!hasTranslation) {
              return false;
            }
            return !existingColumns.some(
              (existingColumn) =>
                column.translation === existingColumn.translation,
            );
          });
          const undefinedTranslations = [
            ...existingColumns,
            ...newColumns,
          ].filter((column) => !column.translation);
          this.uniqueColumns = uniqBy(
            [...existingColumns, ...newColumns],
            'translation',
          );
          this.uniqueColumns = uniqBy(
            [...this.uniqueColumns, ...undefinedTranslations],
            'name',
          );
          this.visibleUniqueColumns = this.uniqueColumns
            .filter((uniqueColumn) =>
              this.fileColumnTitles.includes(uniqueColumn.name),
            )
            .map((uniqueColumn) => {
              const isAccountField = accountFieldsList.includes(
                uniqueColumn.translation,
              );
              const mappedValuesCount = uniqueColumn.values.filter(
                (value) => !!value.translation[0],
              ).length;
              return {
                ...uniqueColumn,
                hasBeenReviewed: false,
                ...(!isAccountField && { mappedValuesCount }),
              };
            })
            .sort(
              (a, b) =>
                Number(accountFieldsList.includes(b.translation)) -
                Number(accountFieldsList.includes(a.translation)),
            );
          this.columnsWithUniqueValues = this.uniqueColumns.filter((column) => {
            if (uniq(column.values.map((value) => value.name))) {
              return column;
            }
            return;
          });
          this.columnsWithUpdatedSourcePaths = this.uniqueColumns.filter(
            (column) => column.hasSourcePathChanged,
          );
        }),
        finalize(() => {
          if (!isEmpty(this.columnsWithUpdatedSourcePaths)) {
            this.updateExistingAttributeMappings();
          }
          this.isLoading = false;
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          this.isLoading = false;
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  updateExistingAttributeMappings() {
    const updatedAttributeBodies = this.columnsWithUpdatedSourcePaths.map(
      (column) => {
        const destination = getDestinationPath(this.group, column);
        const columnValues = buildValueTransformations(
          column.values,
          column.translation,
        );
        return {
          _id: column.attributeId,
          destination_path: column.translation,
          group: this.group._id,
          mapping_type: 'admin-accounts-csv',
          source_path: [column.name],
          value_transformations: columnValues,
          ...(destination.customSegmentTranslation && {
            custom_segment: destination.customSegmentTranslation,
          }),
        };
      },
    );
    from(updatedAttributeBodies)
      .pipe(
        mergeMap((updatedAttributeBody) =>
          this.groupsBackend.v3.putGroupAttributeMapping(
            omit(
              updatedAttributeBody,
              '_id',
            ) as Partial<AttributeMappingPutBody> as AttributeMappingPutBody,
            updatedAttributeBody._id,
            this.group._id,
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  getAllMappingOptions(): MappingOption[] {
    const segmentOptions = (this.group?.custom_segments ?? []).map(
      (customSegment) => ({
        label: customSegment.title,
        value: customSegment._id,
      }),
    );
    const hiddenSegmentOptions = (this.group?.hidden_custom_segments ?? []).map(
      (customSegment) => ({
        label: customSegment.title,
        value: customSegment._id,
      }),
    );
    return [
      ...accountFieldOptions,
      ...segmentOptions,
      ...groupFieldOptions,
      ...hiddenSegmentOptions,
    ];
  }

  openMappingSelectionDrawer(column: VisibleUniqueColumn) {
    this.attributeMappingDrawerService
      .openDrawer({
        group: this.group,
        hasSourcePathChanged: column.hasSourcePathChanged,
        importedColumn: column,
      })
      .afterClose.pipe(
        filter((response: AttributeMapResponse) => !!response.updatedColumn),
        tap((response: AttributeMapResponse) => {
          this.group = response.group;
          const columnIndex = this.visibleUniqueColumns.findIndex(
            (visibleColumn) => column.name === visibleColumn.name,
          );
          const mappedValuesCount = response.updatedColumn.values.filter(
            (value) => !isEmpty(value.translation),
          ).length;
          const isAccountField = accountFieldsList.includes(
            response.updatedColumn.translation,
          );
          this.visibleUniqueColumns[columnIndex] = {
            ...response.updatedColumn,
            hasBeenReviewed: true,
            ...(!isAccountField && { mappedValuesCount }),
          };
          this.visibleUniqueColumns = [...this.visibleUniqueColumns];
          const index = this.newAutomappedColumns.findIndex(
            (column) => response.updatedColumn.name === column.name,
          );
          this.newAutomappedColumns.slice(index, 1);
          if (this.group.roles?.length !== response.group.roles?.length) {
            this.group = { ...this.group, roles: response.group.roles };
          }
          if (
            this.group.custom_segments?.length !==
            response.group.custom_segments?.length
          ) {
            this.group.custom_segments = response.group.custom_segments;
          }
          if (
            this.group.hidden_custom_segments?.length !==
            response.group.hidden_custom_segments?.length
          ) {
            this.group.hidden_custom_segments =
              response.group.hidden_custom_segments;
          }
        }),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  checkAllColumnsHaveBeenApproved(): boolean {
    return this.visibleUniqueColumns?.every((column) => {
      if (!column.skip) {
        return column.hasBeenReviewed;
      }
      return true;
    });
  }

  canApproveAll(): boolean {
    return this.visibleUniqueColumns?.reduce((acc, column) => {
      if (!column.skip) {
        return !!column.translation && acc;
      }
      return acc;
    }, true);
  }

  approveAllAttributeMappings() {
    this.visibleUniqueColumns.forEach((uniqueColumn, index) => {
      this.visibleUniqueColumns[index].hasBeenReviewed = true;
    });
  }

  setSkipStatus(name: UniqueColumn['name'], skipStatus: boolean) {
    const columnIndex = (this.visibleUniqueColumns || []).findIndex(
      (visibleColumn) => name === visibleColumn.name,
    );
    if (0 > columnIndex) {
      return;
    }
    this.uniqueColumns[columnIndex].skip = skipStatus;
    this.visibleUniqueColumns[columnIndex].skip = skipStatus;
  }

  getUnmappedValues(columns: VisibleUniqueColumn[]) {
    return columns.reduce(
      (acc, visibleUniqueColumn) =>
        visibleUniqueColumn.skip || visibleUniqueColumn.hasBeenReviewed
          ? acc
          : [...acc, visibleUniqueColumn.name],
      [],
    );
  }

  openHasMissingMappingDialog() {
    const unmappedValues = this.getUnmappedValues(this.visibleUniqueColumns);
    this.listValuesDialog
      .openDialog({
        confirmButton: $localize`:@@csvAttributeMappings.unmappedValues.confirmButton:Continue reviewing`,
        content: $localize`:@@csvAttributeMappings.unmappedValues.body:Review and approve the following fields, or skip importing them into 10KC.`,
        list: unmappedValues,
        tableTitle: $localize`:@@csvAttributeMappings.unmappedValues.tableTitle:Fields with pending reviews (${unmappedValues.length})`,
        title: $localize`:@@csvAttributeMappings.unmappedValues.title:Review and approve fields`,
      })
      .afterClosed()
      .pipe(
        finalize(() => {
          this.unReviewedFields = unmappedValues.join(', ');
          this.showBanner = true;
        }),
      )
      .subscribe();
    return false;
  }

  showConfirmationDialog() {
    const continueSubmittingMappings =
      this.checkAllColumnsHaveBeenApproved() ||
      this.openHasMissingMappingDialog();
    if (!continueSubmittingMappings) {
      return;
    }
    this.showBanner = false;
    let canUpload = false;
    this.dialogConfirmPrompt
      .openDialog({
        cancel: $localize`:@@csvAttribute.submittedDialog.cancel:Resume editing`,
        color: 'primary',
        confirm: $localize`:@@csvAttribute.submittedDialog.confirm:Confirm and continue`,
        content: $localize`:@@csvAttribute.submittedDialog.content:You can't go back here and change mappings after you click Confirm and continue.`,
        title: $localize`:@@csvAttribute.submittedDialog.Title:Done reviewing mappings?`,
      })
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        concatMap((data) => {
          canUpload = !!data;
          const attributeMappingPostBodies = this.newAutomappedColumns.map(
            (column) => {
              const destination = getDestinationPath(this.group, column);
              return {
                destination_path: destination.type,
                group: this.group._id,
                mapping_type: 'admin-accounts-csv',
                source_path: [column.name],
                value_transformations: [],
                ...(destination.customSegmentTranslation && {
                  custom_segment: destination.customSegmentTranslation,
                }),
              };
            },
          );
          return from(attributeMappingPostBodies).pipe(
            mergeMap((attributeMapping) =>
              this.groupsBackend.v3.postGroupAttributeMapping(
                attributeMapping as Partial<AttributeMappingPostBody> as AttributeMappingPostBody,
                this.group._id,
              ),
            ),
          );
        }),
        finalize(() => {
          if (canUpload) {
            this.uploadCsvFile();
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError(getServerError(error));
          return EMPTY;
        }),
      )
      .subscribe();
  }

  uploadCsvFile() {
    this.viewChange.emit('upload_file');
  }

  isRequired(columnTranslation: string) {
    if (!columnTranslation) {
      return false;
    }
    return ['first_name', 'last_name', 'email'].includes(columnTranslation);
  }
}
